import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  signal,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { timer } from 'rxjs';
import {
  makeNegotiationScene,
  NegotiationScene,
} from 'src/app/phaser/scenes/negotiation.scene';
import { GameInstanceService } from 'src/app/services/game-instance.service';
import { AudioService } from 'src/app/services/audio.service';
import { GameQuery } from 'src/app/state/gameState/game.query';
import { GameService } from 'src/app/state/gameState/game.service';
import { ITopic, IUser } from 'src/app/models/api.model';
import { UserQuery } from 'src/app/state/userState/user.query';
import { EmptyScene, makeEmptyScene } from 'src/app/phaser/scenes/empty.scene';
import { GameStore } from 'src/app/state/gameState/game.store';
import { Environment, EnvService } from '../../services/env.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-negotiation-phase',
  templateUrl: './negotiation-phase.page.html',
  styleUrls: ['./negotiation-phase.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NegotiationPhasePage implements OnInit {
  @ViewChild('phaserWrapper') phaserWrapper!: ElementRef;

  index = signal(0);
  btnIndex = signal(0);
  dimensionNum = signal<number | undefined>(undefined);
  dimensionId = signal<number | undefined>(undefined);
  topic = signal<ITopic | undefined>(undefined);
  user = signal<IUser | undefined>(undefined);
  userHostId!: string;
  hasActionPoints = signal(false);
  showGoToBreakButton = false;
  isInformational = signal(false);
  showPositionDescriptionModal = signal(false);
  positionDescriptionText = signal('');
  isToggleChecked = toSignal(
    inject(GameInstanceService).toggleChecked$,
    { initialValue: false }
  );

  /** Timer id for the reveal-positions auto-close. Instance field, not module-level. */
  private revealTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    public gameInstanceService: GameInstanceService,
    private router: Router,
    private gameService: GameService,
    public gameQuery: GameQuery,
    public gameStore: GameStore,
    public userQuery: UserQuery,
    public envService: EnvService,
    private audioService: AudioService
  ) {
    this.userQuery.user$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user.set(res);
    });

    this.gameQuery
      .select((state) => state.game?.userGames)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.hasActionPoints.set(
          (response?.find((el) => el.userId === userQuery.getUserId())
            ?.actionPoint ?? 0) >= 2
        );
      });

    this.gameQuery
      .select('positionDescription')
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response) {
          this.showPositionDescriptionModal.set(true);
          this.positionDescriptionText.set(response);
        } else {
          this.showPositionDescriptionModal.set(false);
          this.positionDescriptionText.set('');
        }
      });
  }

  ngOnInit() {
    this.audioService.play('exploreThePossibilities');
    this.gameService.setTypeOfPhase('negotiation');
    this.showGoToBreakButton = this.envService.env === Environment.debug;

    this.userHostId = this.gameQuery.getGameFromState()?.userGames[0].userId;
    this.gameService.startNPCMovements().catch((err) => console.log(err));
    this.gameService.revealPositions(false);
    timer(10000)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.btnIndex.set(1));
    this.gameQuery.selectedDimension$
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.dimensionNum.set(res.value);
        this.dimensionId.set(res.id);
        this.btnIndex.set(1);
      });

    this.gameQuery.topics$.pipe(untilDestroyed(this)).subscribe(() => {
      this.topic.set(this.gameQuery.getCurrentTopic());
      this.isInformational.set(
        this.gameQuery.getCurrentTopicIsInformational() ?? false
      );
    });
  }

  testLog(e) {
    console.log(
      'Click position =>',
      this.gameInstanceService.gameInstance.input.mousePointer.x,
      this.gameInstanceService.gameInstance.input.mousePointer.y,
      { e }
    );
  }

  ionViewWillEnter() {
    this.gameInstanceService.negotiationPage.next(true);
    this.gameInstanceService.switchScene(
      NegotiationScene.key,
      makeNegotiationScene
    );
    if (this.gameInstanceService.isZoomBoardOpen) {
      this.gameService.updateModalOpen(true);
    }
    this.gameInstanceService.toggleChecked$.next(false);
  }

  ionViewDidEnter() {
    this.gameInstanceService.gameInstance.scale.on('resize', this.resize, this);
    this.gameInstanceService.gameInstance.scale.refresh();
    this.btnIndex.set(1);
  }

  ionViewWillLeave() {
    this.gameInstanceService.negotiationPage.next(false);
    this.gameInstanceService.switchScene(EmptyScene.key, makeEmptyScene);
    this.gameService.updateModalOpen(false);
    this.gameService.revealPositions(false);
    this.gameInstanceService.toggleChecked$.next(false);
    clearTimeout(this.revealTimeoutId);
    this.btnIndex.set(0);
    this.gameInstanceService.guideDisabled$.next(false);
  }

  openModal() {
    this.index.set(1);
    this.btnIndex.set(0);
  }

  openOverview() {
    this.router.navigate(['/overview']);
  }

  reveal() {
    this.index.set(0);
    setTimeout(() => {
      this.btnIndex.set(2);
    }, 3000);

    const dimensionNum = this.dimensionNum();
    this.gameService.updateDimensionsCalled(dimensionNum);

    if (
      (dimensionNum === 1 &&
        this.gameQuery.getNumOfDimensionsCalled().d1 === 1) ||
      (dimensionNum === 2 &&
        this.gameQuery.getNumOfDimensionsCalled().d2 === 1) ||
      (dimensionNum === 3 &&
        this.gameQuery.getNumOfDimensionsCalled().d3 === 1)
    ) {
      this.gameService.revealPositions(true);

      if (this.hasActionPoints()) {
        // dimensionId is always set alongside dimensionNum from the same subscription.
        this.gameService.sendRevealPositions(this.dimensionId()!);
      }
    }

    clearTimeout(this.revealTimeoutId);

    this.revealTimeoutId = setTimeout(() => {
      this.gameService.revealPositions(false);
      this.btnIndex.set(0);
    }, 20000);
  }
  closeModal() {
    this.index.set(0);
    this.btnIndex.set(1);
  }

  closePositionModal() {
    this.showPositionDescriptionModal.set(false);
    this.positionDescriptionText.set('');
  }
  closeInfo() {
    this.btnIndex.set(0);
    this.gameService.revealPositions(false);
    clearTimeout(this.revealTimeoutId);
  }

  toggleChange(event) {
    this.gameInstanceService.toggleChecked$.next(event.target.checked);
  }

  preventClick(e: any) {
    e.stopPropagation();
  }

  resize() {
    if (this.phaserWrapper) {
      this.phaserWrapper.nativeElement.style.width =
        this.gameInstanceService.gameInstance.canvas.style.width;
      this.phaserWrapper.nativeElement.style.height =
        this.gameInstanceService.gameInstance.canvas.style.height;
      this.phaserWrapper.nativeElement.style.marginLeft =
        this.gameInstanceService.gameInstance.canvas.style.marginLeft;
      this.phaserWrapper.nativeElement.style.marginTop =
        this.gameInstanceService.gameInstance.canvas.style.marginTop;
    }
  }
}
